<template>
  <div class="form-employer text-center">
    <TopMenu
      :title="employerTitle"
      back-to="/dashboard/employers"
      dark-background/>
    <div class="container-fluid content">
      <form class="form-form-employer mt-3">
        <FormInput
          v-validate="'required'"
          :name="$t('EMPLOYERS.COMPANY_NAME')"
          v-model="localEmployer.companyName"
          :placeholder="$t('EMPLOYERS.COMPANY_NAME')"
          required
          class="mb-3"
          type="text"/>
        <FormInput
          v-validate="'required'"
          :name="$t('EMPLOYERS.CONTACT_PERSON')"
          v-model="localEmployer.contactName"
          :placeholder="$t('EMPLOYERS.CONTACT_PERSON')"
          required
          class="mb-3"
          type="text"/>
        <FormInput
          v-validate="'email'"
          :name="$t('EMPLOYERS.EMAIL')"
          v-model="localEmployer.email"
          :placeholder="$t('EMPLOYERS.EMAIL')"
          class="mb-3"
          type="email"/>
        <FormInput
          :name="$t('EMPLOYERS.ADDRESS')"
          v-model="localEmployer.address.street"
          :placeholder="$t('EMPLOYERS.ADDRESS')"
          class="mb-3"
          type="text"/>
        <FormInput
          :name="$t('EMPLOYERS.ADDRESS2')"
          v-model="localEmployer.address.appartment"
          :placeholder="$t('EMPLOYERS.ADDRESS2')"
          class="mb-3"
          type="text"/>
        <FormSelect
          v-model="localEmployer.address.countryCode"
          :placeholder="$t('EMPLOYERS.COUNTRY')"
          :options="formatCountryToOptions()"
          class="mb-3"/>
        <FormSelect
          v-model="localEmployer.address.provinceCode"
          :placeholder="$t('EMPLOYERS.PROVINCE')"
          :options="selectedProvinces"
          class="mb-3" />
        <FormInput
          :name="$t('EMPLOYERS.CITY')"
          v-model="localEmployer.address.city"
          :placeholder="$t('EMPLOYERS.CITY')"
          class="mb-3"
          type="text"/>
        <FormInput
          v-validate="'postalCode'"
          v-mask="customMask"
          :name="$t('EMPLOYERS.POSTAL_CODE')"
          v-model="localEmployer.address.postalCode"
          :placeholder="$t('EMPLOYERS.POSTAL_CODE')"
          class="mb-3"
          type="text" />
        <FormInput
          v-validate="'phoneNumber'"
          v-mask="['(###) ###-####','#(###) ###-####']"
          :name="$t('EMPLOYERS.PHONE')"
          v-model="localEmployer.phoneNumber"
          :placeholder="$t('EMPLOYERS.PHONE')"
          class="mb-3"
          type="tel"/>

        <h3>{{ $t("EMPLOYERS.ACTIVITY_AREA_LABEL") }}</h3>
        <TextAction
          v-for="(activityArea, index) in localEmployer.activityAreas"
          :key="activityArea.id"
          class-name="pt-2"
          type="delete"
          @click="deleteActivityArea(index)">{{ $t(activityArea.name) }}</TextAction>

        <h5
          v-if="availableActivityAreas.length > 0"
          class="pt-2">{{ $t("EMPLOYERS.ADD_ACTIVITY_AREA_LABEL") }}</h5>
        <TextAction
          v-for="(activityArea) in availableActivityAreas"
          :key="activityArea.id"
          class-name="pt-2"
          type="add"
          @click="addActivityArea(activityArea)">{{ $t(activityArea.name) }}</TextAction>

        <ActionButton
          v-if="$route.params.id != 'add'"
          class="btn btn-lg btn-primary btn-block mt-3 mb-3"
          @click="save">{{ $t("EMPLOYERS.SAVE_BUTTON") }}</ActionButton>
        <ActionButton
          v-if="$route.params.id != 'add'"
          class="btn btn-lg btn-danger btn-block mt-3 mb-3"
          @click="showConfirmDelete = true">{{ $t("EMPLOYERS.DELETE_EMPLOYER_BUTTON") }}</ActionButton>
        <ActionButton
          v-if="$route.params.id == 'add'"
          class="btn btn-lg btn-primary btn-block mt-3 mb-3"
          @click="add">{{ $t("EMPLOYERS.ADD_EMPLOYER_BUTTON") }}</ActionButton>
      </form>
    </div>
    <BottomMenu />
    <ConfirmModal
      v-if="$route.params.id != 'add' && showConfirmDelete"
      show-modal
      size="md"
      @cancel="showConfirmDelete = false"
      @confirm="confirmDelete">{{ $t("EMPLOYERS.CONFIRM_MODAL_DELETE_EMPLOYERS") }}</ConfirmModal>
  </div>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep';
import { mapState } from 'vuex';
import TopMenu from '@/components/Menu/TopMenu';
import BottomMenu from '@/components/Menu/BottomMenu';
import TextLink from '@/components/Utils/TextLink';
import ActionButton from '@/components/Actions/ActionButton';
import FormInput from '@/components/Forms/FormInput';
import FormSelect from '@/components/Forms/FormSelect';
import ConfirmModal from '@/components/Modal/ConfirmModal';
import TextAction from '@/components/Utils/TextAction';

export default {
  name: 'FormEmployer',
  components: {
    ActionButton,
    TextLink,
    TopMenu,
    BottomMenu,
    FormInput,
    FormSelect,
    ConfirmModal,
    TextAction,
  },
  data() {
    return {
      showConfirmDelete: false,
      localEmployer: {
        address: {},
        activityAreas: [],
      },
      customMask: {
        mask: 'UUUUUU',
        tokens: {
          U: {
            pattern: /[0-9a-zA-Z]/,
            transform: v => v.toUpperCase(),
          },
        },
      },
    };
  },
  computed: {
    ...mapState('localization', ['countriesWithProvinces']),
    ...mapState('employer', ['selectedEmployer']),
    ...mapState('activityArea', ['activityAreas']),
    isAdd() {
      return this.$route.params.id === 'add';
    },
    selectedProvinces: {
      get() {
        if (!this.localEmployer.address.countryCode) {
          return [];
        }
        const country = this.countryList.filter(
          c => c.code === this.localEmployer.address.countryCode,
        )[0];
        if (!country) {
          return [];
        }
        return country.provinces
          .map(province => ({
            value: province.code,
            text: this.$t(`PROVINCE.${province.code}`),
          })).sort((a, b) => a.text.toLowerCase().localeCompare(b.text.toLowerCase()));
      },
    },
    availableActivityAreas: {
      get() {
        if (this.activityAreas && this.localEmployer.activityAreas) {
          return this.activityAreas.filter((availableActivityArea) => {
            let available = true;
            this.localEmployer.activityAreas.forEach((activityArea) => {
              if (activityArea.id === availableActivityArea.id) {
                available = false;
              }
            });

            return available;
          });
        }

        return [];
      },
    },
    employerTitle() {
      return this.isAdd
        ? this.$t('EMPLOYERS.EMPLOYERS_LABEL_ADD')
        : this.$t('EMPLOYERS.EMPLOYERS_LABEL_EDIT');
    },
  },
  watch: {
    countriesWithProvinces: {
      handler(newVal) {
        if (newVal) {
          this.countryList = newVal;
        } else {
          this.countryList = [];
        }
      },
      immediate: true,
    },
    selectedEmployer: {
      handler(newVal) {
        if (newVal) {
          this.localEmployer = cloneDeep(newVal);
        } else {
          this.localEmployer = {
            address: {},
            activityAreas: [],
          };
        }
      },
      immediate: true,
    },
  },
  created() {
    this.$store
      .dispatch('localization/countriesWithProvinces')
      .then(() => {
        this.$store.dispatch('activityArea/activityAreas');
      })
      .then(() => {
        if (this.$route.params.id !== 'add') {
          this.$store.dispatch(
            'employer/currentMemberEmployer',
            this.$route.params.id,
          );
        } else {
          this.localEmployer = {
            address: { countryCode: 'CA', provinceCode: 'CA-QC' },
            activityAreas: [],
          };
        }
      });
  },
  methods: {
    formatPostalCode() {
      if (
        this.localEmployer
        && this.localEmployer.address
        && this.localEmployer.address.postalCode
      ) {
        this.localEmployer.address.postalCode = this.localEmployer.address.postalCode
          .replace(/\s/g, '')
          .toUpperCase();
      }
    },
    add() {
      this.formatPostalCode();
      this.$validator.validate().then(() => {
        if (this.$validator.errors.items.length > 0) {
          this.$store.commit('alerts/setAlert', {
            msg: this.$t(
              `VALIDATION.${
                this.$validator.errors.items[0].rule.toUpperCase()}`,
              [this.$validator.errors.items[0].field],
            ),
          });
        } else {
          this.$store
            .dispatch('employer/addMemberEmployer', this.localEmployer)
            .then(() => {
              this.$router.push('/dashboard/employers');
              this.$store.commit('alerts/setAlert', {
                msg: 'EMPLOYERS.CONFIRMATION_MESSAGE',
              });
            });
        }
      });
    },
    save() {
      this.formatPostalCode();
      this.$validator.validate().then(() => {
        if (this.$validator.errors.items.length > 0) {
          this.$store.commit('alerts/setAlert', {
            msg: this.$t(
              `VALIDATION.${
                this.$validator.errors.items[0].rule.toUpperCase()}`,
              [this.$validator.errors.items[0].field],
            ),
          });
        } else {
          this.$store
            .dispatch('employer/saveMemberEmployer', {
              id: this.$route.params.id,
              localEmployer: this.localEmployer,
            })
            .then(() => {
              this.$store.commit('alerts/setAlert', {
                msg: 'EMPLOYERS.CONFIRMATION_MESSAGE',
              });
            });
        }
      });
    },
    formatCountryToOptions() {
      return this.countryList.map(country => ({
        value: country.code,
        text: this.$t(`COUNTRY.${country.code}`),
      }));
    },
    confirmDelete() {
      this.showConfirmDelete = false;
      this.$store
        .dispatch('employer/deleteMemberEmployer', this.$route.params.id)
        .then(() => {
          this.showConfirmDelete = false;
          this.$router.push('/dashboard/employers');
          this.$store.commit('alerts/setAlert', {
            msg: 'EMPLOYERS.CONFIRM_DELETE_MESSAGE',
          });
        });
    },
    addActivityArea(activityArea) {
      this.localEmployer.activityAreas.push(activityArea);
    },
    deleteActivityArea(index) {
      this.localEmployer.activityAreas.splice(index, 1);
    },
  },
};
</script>

<style scoped lang="scss">
div {
  &.form-employer {
    height: 100%;
  }
  &.content {
    padding-top: $top-menu-height;
    padding-bottom: $bottom-menu-height;
    background-color: $base;
    min-height: 100%;
  }
}

h3,
h5 {
  color: $primary;
}
</style>
